import {
  NewDeviceChallenge,
  NewDeviceCodeAction,
  NewDeviceConfirmationReason,
} from './types';

/**
 * What a new-device challenge reason means for the sign-in code step
 */
export type NewDeviceReasonBehavior = {
  /**
   * Where the reason's message goes: the status line (`error` styles it as
   * one), the code field, or in place of the form when only a password reset
   * can get the user in
   */
  shows: 'status' | 'error' | 'field' | 'reset';
  /**
   * The copy for the reason. The UI keys off `reason`; the backend's message
   * is only used for a reason this version doesn't know.
   */
  message: (
    challenge: NewDeviceChallenge,
    lastAction?: NewDeviceCodeAction
  ) => string;
  /**
   * What happened to the code:
   * - emailed: a new code was sent
   * - dead: the typed code is dead and none was sent, so a resend is allowed
   *   at once
   * - kept: the code and the resend cooldown are unchanged
   */
  code: 'emailed' | 'dead' | 'kept';
  /**
   * Whether the copy can say a code was emailed; the first send may have
   * failed
   */
  codeWasSent: boolean;
};

export const NEW_DEVICE_REASONS: Record<
  NewDeviceConfirmationReason,
  NewDeviceReasonBehavior
> = {
  new_device: {
    shows: 'status',
    message: (_challenge, lastAction) =>
      lastAction === 'resend' ? 'We emailed you a new code.' : '',
    code: 'emailed',
    codeWasSent: true,
  },
  code_invalid: {
    shows: 'field',
    message: () => "That code isn't right. Check the email and try again.",
    code: 'kept',
    codeWasSent: true,
  },
  code_locked: {
    shows: 'field',
    message: () => 'Too many incorrect codes. Send a new code.',
    code: 'dead',
    codeWasSent: true,
  },
  code_expired: {
    shows: 'field',
    message: () => 'That code expired. We sent a new one to your email.',
    code: 'emailed',
    codeWasSent: true,
  },
  code_limit: {
    shows: 'reset',
    message: () => 'Too many incorrect codes. Reset your password to sign in.',
    code: 'kept',
    codeWasSent: true,
  },
  send_capped: {
    shows: 'reset',
    message: () =>
      "We've sent the most codes we can today. Reset your password to sign in.",
    code: 'kept',
    codeWasSent: true,
  },
  unavailable: {
    shows: 'error',
    message: () =>
      "We couldn't send or check a code right now. Try again in a few minutes.",
    code: 'kept',
    codeWasSent: false,
  },
  unknown: {
    shows: 'status',
    message: challenge =>
      challenge.message ||
      "We couldn't confirm this device. Try again in a few minutes.",
    code: 'kept',
    codeWasSent: false,
  },
};
